import { useEffect } from 'react';
import { css } from '@emotion/react';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { QuickFillHooks } from '@frontend/api-quick-fill';
import { checkAggregateACL } from '@frontend/auth-helpers';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { useSearch, type SearchParams } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import { Tabs } from '@frontend/design-system';
import { QUICK_FILL_TRACKING_IDS } from '../../tracking-ids';
import { useSetRecipientCounts } from './hooks';
import { ManageListTabContent } from './manage-list';
import { SendMessagesTabContent } from './send-messages';
import { useQuickFillRecipientShallowStore } from './store';

enum PanelTabs {
  SendMessages = 'send-messages',
  ManageList = 'manage-list',
}

const trackingIds = QUICK_FILL_TRACKING_IDS.sidePanel;

export const TrayContent = () => {
  const { t } = useTranslation('schedule-quick-fill');
  const params = useSearch({ strict: false }) as SearchParams<{ quickFillTray: PanelTabs }>;
  const { setValidLocationIds } = useQuickFillRecipientShallowStore('setValidLocationIds');
  const { selectedLocationIds } = useAppScopeStore();
  const { locationIdWiseQuickFillFeature } = QuickFillHooks.useQuickFillFeatureDetails();
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const canSendMessages = getFeatureFlagValue('nwx:messaging-integrated-messaging-fe-acl-enforcement', 'ALL')
    ? !!checkAggregateACL(selectedLocationIds, Permission.SMS_INBOX_MANAGE, 'any')?.aggregateValue
    : true;
  // Set recipient counts for locations
  useSetRecipientCounts();

  // Set valid location ids
  useEffect(() => {
    if (locationIdWiseQuickFillFeature) {
      const validLocationIds = selectedLocationIds.filter(
        (locationId) => locationIdWiseQuickFillFeature[locationId] === true
      );
      setValidLocationIds(validLocationIds);
    }
  }, [locationIdWiseQuickFillFeature, selectedLocationIds]);

  return (
    <Tabs initialTab={canSendMessages ? params?.quickFillTray ?? PanelTabs.SendMessages : PanelTabs.ManageList}>
      <Tabs.Bar css={{ padding: theme.spacing(0, 3) }}>
        <Tabs.Tab
          id={PanelTabs.SendMessages}
          trackingId={trackingIds.sendMessageTabButton}
          disabled={!canSendMessages}
          hoverLabel={canSendMessages ? undefined : t('You do not have permission to send text messages')}
        >
          {t('Send Messages')}
        </Tabs.Tab>
        <Tabs.Tab id={PanelTabs.ManageList} trackingId={trackingIds.manageListTabButton}>
          {t('Manage List')}
        </Tabs.Tab>
      </Tabs.Bar>
      <Tabs.Panel css={tabContentStyle} controller={PanelTabs.SendMessages} keepMounted={false}>
        <SendMessagesTabContent />
      </Tabs.Panel>
      <Tabs.Panel css={tabContentStyle} controller={PanelTabs.ManageList} keepMounted={false}>
        <ManageListTabContent />
      </Tabs.Panel>
    </Tabs>
  );
};

const tabContentStyle = css`
  height: 100%;
  overflow-y: auto;
  background-color: ${theme.colors.neutral5};
  padding: ${theme.spacing(4, 3)};
`;
